import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Settings } from "lucide-react";
import Layout2 from "@/components/Layout/Layout2";
import { toast } from "react-hot-toast";
import {
  getEndOfDayReportServiceType,
  getEndOfDayReportDetails,
  getEndOfDayReportItemSales,
  printEndOfDayReportServiceType,
  printEndOfDayReportSummary,
  printEndOfReport,
  printGrandReport,
} from "@/api/reporting";
import {
  buildReportCsvName,
  buildSummaryRows,
  downloadCsv,
  formatCount,
  formatCurrency,
  getTodayInputDate,
} from "./reporting-utils";
import { useReportTills } from "./use-report-tills";
import hasPermission from "@/hooks/hasPermission";
import useAuth from "@/hooks/useAuth";

const REPORT_CARDS = [
  {
    title: "Grand Report",
    viewPath: "/reporting/grand-report",
    actionLabel: "Print",
    gridClass: "xl:col-start-2 xl:row-start-1",
    viewPermission: "reporting.reporting.view_grand_report",
    actionPermission: "reporting.reporting.print_grand_print",
  },
  {
    title: "End of Day Report/Z (Summary)",
    viewPath: "/reporting/end-of-day-z-summary",
    actionLabel: "Print",
    gridClass: "xl:row-start-2 xl:col-start-1",
    viewPermission: "reporting.reporting.view_end_of_day",
    actionPermission: "reporting.reporting.print_end_of_day_report",
  },
  {
    title: "End of Day Report/Z (Details)",
    viewPath: "/reporting/end-of-day-z-details",
    actionLabel: "Download",
    gridClass: "xl:row-start-2 xl:col-start-2",
    viewPermission: "reporting.reporting.view_end_of_day_report_details",
    actionPermission: "reporting.reporting.download_end_of_day_report_details",
  },
  {
    title: "End of Day Report/Z (Service Type)",
    viewPath: "/reporting/end-of-day-z-service-type",
    actionLabel: "Download",
    gridClass: "xl:row-start-2 xl:col-start-3",
    viewPermission: "reporting.reporting.end_of_day_report_z",
    actionPermission: "reporting.reporting.end_of_day_report_z_print",
  },
  {
    title: "EOD Sales Report/Z (Sale)",
    viewPath: "/reporting/end-of-day-z-sale",
    actionLabel: "Download",
    gridClass: "xl:row-start-3 xl:col-start-2",
    viewPermission: "reporting.reporting.eod_sales_report_view",
    actionPermission: "reporting.reporting.eod_sales_report_print",
  },
  {
    title: "End of Shift   Report/X",
    viewPath: "/reporting/end-of-day-x",
    actionLabel: "Print",
    gridClass: "xl:row-start-4 xl:col-start-2",
    viewPermission: "reporting.reporting.end_of_day_report_x_view",
    actionPermission: "reporting.reporting.end_of_day_report_x_print",
  },
];

function Reporting() {
  const [actionLoading, setActionLoading] = useState({});
  const { tills } = useReportTills();
  const defaultTillId = useMemo(() => null, []);
  const {user} = useAuth()

  const setCardLoading = (title, value) => {
    setActionLoading((prev) => ({ ...prev, [title]: value }));
  };

  const getDefaultParams = () => ({
    date: getTodayInputDate(),
    till_id: defaultTillId,
  });

  const handleCardAction = async (card) => {
    const params = getDefaultParams();
    setCardLoading(card.title, true);

    try {
      if (card.title === "Grand Report") {
        const response = await printGrandReport();
        if (response?.status === "success") {
          toast.success(
            response?.message || "Grand report print dispatched successfully."
          );
        } else {
          toast.error(response?.message || "Failed to print grand report.");
        }
        return;
      }

      if (card.title === "End of Day Report/Z (Summary)") {
        const response = await printEndOfDayReportSummary();
        if (response?.status === "success") {
          toast.success(
            response?.message || "Summary print dispatched successfully."
          );
        } else {
          toast.error(response?.message || "Failed to print summary report.");
        }
        return;
      }

      if (card.title === "End of Day Report/Z (Service Type)") {
        const response = await getEndOfDayReportServiceType(params);
        if (response?.status !== "success") {
          toast.error(response?.message || "Failed to download service type report.");
          return;
        }

        const data = response?.data || {};
        const summaryRows = buildSummaryRows({
          items: data.service_types,
          netTotal: data.net_total,
        });
        if (!summaryRows.length) {
          toast.error("No data available to export.");
          return;
        }

        const fileName = buildReportCsvName(
          "end-of-day-z-service-type",
          params.date,
          params.till_id
        );
        const csvRows = summaryRows.map((row) => [row.label, row.count, row.value]);
        downloadCsv(
          fileName,
          ["Service type", "Number of Order/ Transactions", "Total Value"],
          csvRows
        );
        return;
      }

      if (card.viewPath === "/reporting/end-of-day-x" || card.title.includes("Report/X")) {
        const response = await printEndOfReport();
        if (response?.status === "success") {
          toast.success(
            response?.message || "Report print dispatched successfully."
          );
        } else {
          toast.error(response?.message || "Failed to print end of report.");
        }
        return;
      }

      if (card.title === "End of Day Report/Z (Details)") {
        const response = await getEndOfDayReportDetails(params);
        if (response?.status !== "success") {
          toast.error(response?.message || "Failed to download report details.");
          return;
        }

        const payload = response?.data || {};
        const items = payload.data || [];
        if (!items.length) {
          toast.error("No data available to export.");
          return;
        }

        const normalizedRows = items.map((item, index) => ({
          id: item.order_id || item.id || item.orderId || `#${index + 1}`,
          items:
            item.items_count ??
            item.number_of_items ??
            item.items ??
            item.quantity ??
            0,
          cnp: item.cnp ?? item.online ?? item.cnp_total ?? 0,
          pbl: item.pbl ?? item.pay_by_link ?? item.pbl_total ?? 0,
          card: item.card ?? item.card_total ?? 0,
          cash: item.cash ?? item.cash_total ?? 0,
          total: item.total_value ?? item.total ?? item.total_price ?? 0,
        }));

        const fileName = buildReportCsvName(
          "end-of-day-z-details",
          params.date,
          params.till_id
        );
        const csvRows = normalizedRows.map((row) => [
          row.id,
          formatCount(row.items),
          formatCurrency(row.cnp),
          formatCurrency(row.pbl),
          formatCurrency(row.card),
          formatCurrency(row.cash),
          formatCurrency(row.total),
        ]);
        downloadCsv(
          fileName,
          ["Order Id", "Number of Items", "CNP", "PBL", "Card", "Cash", "Total value"],
          csvRows
        );
        return;
      }

      if (card.title === "EOD Sales Report/Z (Sale)") {
        const response = await getEndOfDayReportItemSales(params);
        if (response?.status !== "success") {
          toast.error(response?.message || "Failed to download item sales report.");
          return;
        }

        const payload = response?.data || {};
        const items = payload.data || [];
        if (!items.length) {
          toast.error("No data available to export.");
          return;
        }

        const normalizedRows = items.map((item, index) => ({
          name: item.item_name || item.name || `Item ${index + 1}`,
          quantity:
            item.total_quantity ??
            item.quantity ??
            item.qty ??
            item.count ??
            0,
          price:
            item.price ?? item.unit_price ?? item.each_price ?? item.unit ?? 0,
          total: item.total_value ?? item.total ?? item.total_price ?? 0,
        }));

        const fileName = buildReportCsvName(
          "end-of-day-z-sale",
          params.date,
          params.till_id
        );
        const csvRows = normalizedRows.map((row) => [
          row.name,
          formatCount(row.quantity),
          formatCurrency(row.price),
          formatCurrency(row.total),
        ]);
        downloadCsv(
          fileName,
          ["Item Name", "Total Sale Quantity", "Each Price", "Total Sale Price"],
          csvRows
        );
      }
    } finally {
      setCardLoading(card.title, false);
    }
  };

  const filteredCards = useMemo(() => {
    return REPORT_CARDS.filter(
      (card) =>
        (!card.viewPermission || hasPermission(card.viewPermission)) ||
        (!card.actionPermission || hasPermission(card.actionPermission))
    );
  }, []);

  return (
    <Layout2
      pageName={{ icon: true, title: "Reporting" }}
      paths={[
        { name: "Home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Reporting", path: "/reporting" },
      ]}
    >
      <div className="px-4 pb-12">
        <div className="flex justify-end">
          <Link
            to="/reporting/settings"
            className="flex items-center gap-2 text-cyellow text-sm font-semibold"
          >
            <Settings size={16} />
            Settings
          </Link>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3 xl:gap-8">
          {filteredCards.map((card) => (
            <div
              key={card.title}
              className={`rounded-md border border-cblack6 bg-cblack4 px-6 py-5 shadow-md ${
                card.gridClass || ""
              }`}
            >
              <div className="text-center text-sm font-semibold text-cwhite">
                {card.title}
              </div>
              <div className="mt-4 flex items-center gap-3">
                {(!card.viewPermission || hasPermission(card.viewPermission)) && (
                  <Link
                    to={card.viewPath}
                    className="flex-1 rounded bg-color7 py-2 text-center text-sm font-semibold text-cblack"
                  >
                    View
                  </Link>
                )}
                {(!card.actionPermission || hasPermission(card.actionPermission)) && (
                  <button
                    type="button"
                    disabled={actionLoading[card.title]}
                    onClick={() => handleCardAction(card)}
                    className="flex-1 rounded bg-color6 py-2 text-center text-sm font-semibold text-cblack disabled:opacity-60"
                  >
                    {actionLoading[card.title]
                      ? card.actionLabel === "Print"
                        ? "Printing..."
                        : "Downloading..."
                      : card.actionLabel}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout2>
  );
}

export default Reporting;